<!--
  FAQ page
  Operator can search or add FAQ on this page
-->
<template>
  <div class="page-content faq-list">
    <indicate-right-panel-colors />

    <hr class="mt-5">

    <div class="actions-wrapper">
      <el-input
        id="query"
        v-model="searchKey"
        placeholder="Type something"
        @change="filterFaqs"
      >
        <template #prepend>
          <i class="fa fa-search" />
        </template>
      </el-input>

      <p-button
        v-if="isEditable"
        class="ml-3"
        type="purple"
        round
        @click="onCreateFaq"
      >
        Add New FAQ
      </p-button>
    </div>

    <el-radio-group v-model="category">
      <el-radio-button label="">
        All
      </el-radio-button>
      <el-radio-button
        v-for="(item, index) in faqCategories"
        :key="index"
        :label="item.key"
      >
        {{ item.label }}
      </el-radio-button>
    </el-radio-group>

    <div class="custom-collapse">
      <el-collapse>
        <faq-item
          v-for="(item, index) in filteredFaqs"
          :key="index"
          :operator-faq="item"
          :editable="isEditable"
          @edit="onEditFaq(item.id)"
          @delete="onDeleteFaq(item.id)"
          @view-changes="onViewChanges(item.id)"
        />
      </el-collapse>
    </div>

    <add-new-faq-dialog
      v-model="faq"
      :is-show-dialog="isShowCreateDialog"
      @submit="onCreateUpdateFaq"
      @cancel="isShowCreateDialog = false"
    />

    <view-changes-dialog
      :is-show-dialog="isShowChangesDialog"
      :changes="changes"
      @cancel="isShowChangesDialog = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, toRefs, computed, watch, onMounted, defineAsyncComponent } from 'vue';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { useProfile } from '@/use/profile';
import { useSwalDialog } from '@/use/swalDialog';
import { OperatorFaq, OperatorFaqChange } from '@/models/operatorFaq';
import { faqCategories } from '@/constants/faq';

import * as operatorFaqService from '@/services/operatorFaq';

const IndicateRightPanelColors = defineAsyncComponent(() => import('./OperatorFaq/IndicateRightPanelColors.vue') as any);
const FaqItem = defineAsyncComponent(() => import('./OperatorFaq/FaqItem.vue') as any);
const ViewChangesDialog = defineAsyncComponent(() => import('./OperatorFaq/ViewChangesDialog.vue') as any);
const AddNewFaqDialog = defineAsyncComponent(() => import('../Dialogs/AddNewFaq.vue') as any);

const paginationLimit: number = 30;

const { userRoles } = useProfile();

const { isAdmin, isSupervisor, isDirector, isTeamLead } = toRefs(userRoles);

const { showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();

const category = ref('');
const searchKey = ref('');
const totalCount = ref(0);
const isShowCreateDialog = ref(false);
const isShowChangesDialog = ref(false);

const faq: Ref<OperatorFaq> = ref({
  id: '',
  categories: [],
  question: '',
  answer: '',
  createdAt: new Date(),
});

const changes: Ref<OperatorFaqChange[]> = ref([]);

const faqList: Ref<OperatorFaq[]> = ref([]);
const filteredFaqs: Ref<OperatorFaq[]> = ref([]);

const isEditable = computed(() => isAdmin.value || isSupervisor.value || isDirector.value || isTeamLead.value);

const loadAllFAQs = async () => {
  const loading = ElLoading.service({
    lock: true,
    text: 'Loading FAQs...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  faqList.value = [];
  totalCount.value = 0;

  await getFAQs();

  filteredFaqs.value = faqList.value;
  loading.close();
};

/*
  This function is recursive function
  because the API returns response regarding with pagination limit and
  we don't know total count, but we need to get all FAQs
*/
const getFAQs = async (offset: number = 0) => {
  try {
    const opFaqsData = await operatorFaqService.fetchOperatorFaqs({
      orderBy: 'createdAt',
      pagination: {
        limit: paginationLimit,
        offset,
      },
    });

    for (const faq of opFaqsData.rows) {
      faqList.value.push(faq);
    }

    totalCount.value += opFaqsData.rows.length;

    // Until total count is reached to the count
    if (totalCount.value < opFaqsData.count) {
      await getFAQs(offset + paginationLimit);
    }
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
};

const filterFaqs = () => {
  const categoriedFaqs = category.value
    ? faqList.value.filter((item: OperatorFaq) => item.categories?.includes(category.value))
    : faqList.value;

  if (!searchKey.value) {
    filteredFaqs.value = categoriedFaqs;
    return;
  }

  const regex = new RegExp(
    `(<a[^>]*href\\s*=\\s*["'])(?:(?:(?!\\1).)*${searchKey.value}(?:(?!\\1).)*)["'][^>]*>|(${searchKey.value})`,
    'gi',
  );
  filteredFaqs.value = categoriedFaqs
    .filter((faqItem: OperatorFaq) => {
      let matchWithQuestion: RegExpExecArray | null;
      while ((matchWithQuestion = regex.exec(faqItem.question)) !== null) {
        if (matchWithQuestion[2]) {
          return true;
        }
      }

      let matchWithAnswer: RegExpExecArray | null;
      while ((matchWithAnswer = regex.exec(faqItem.answer)) !== null) {
        if (matchWithAnswer[2]) {
          return true;
        }
      }

      return false;
    })
    .map((faqItem: OperatorFaq) => {
      // Format html text to show highlight for search key
      const highlightedQuestion = faqItem.question.replace(regex, (match, group1, group2) => {
        if (group2) {
          return `<span class="highlight">${group2}</span>`;
        }

        return match;
      });

      const highlightedAnswer = faqItem.answer.replace(regex, (match, group1, group2) => {
        if (group2) {
          return `<span class="highlight">${group2}</span>`;
        }

        return match;
      });

      return {
        ...faqItem,
        question: highlightedQuestion,
        answer: highlightedAnswer,
      };
    });
};

const onCreateFaq = () => {
  faq.value = {
    id: '',
    categories: [],
    question: '',
    answer: '',
    relatedQuestions: [],
    createdAt: new Date(),
  };

  isShowCreateDialog.value = true;
};

const onEditFaq = (id: string) => {
  faq.value = faqList.value.find((item: OperatorFaq) => item.id === id)!;
  isShowCreateDialog.value = true;
};

const onCreateUpdateFaq = async () => {
  isShowCreateDialog.value = false;

  const loading = ElLoading.service({
    lock: true,
    text: faq.value.id ? 'Updating FAQ...' : 'Creating FAQ...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    if (faq.value.id) {
      await updateFaq();
    } else {
      await createFaq();
    }
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    loading.close();
  }

  filterFaqs();
};

const createFaq = async () => {
  const newFaq = await operatorFaqService.createOperatorFaq({
    answer: faq.value.answer,
    question: faq.value.question,
    categories: faq.value.categories,
    relatedQuestions: faq.value.relatedQuestions,
  });

  faqList.value.push(newFaq);
};

const updateFaq = async () => {
  const updatedFaq = await operatorFaqService.updateOperatorFaq(faq.value.id, {
    categories: faq.value.categories,
    question: faq.value.question,
    answer: faq.value.answer,
    relatedQuestions: faq.value.relatedQuestions,
  });

  const index = faqList.value.findIndex((item: OperatorFaq) => item.id === faq.value.id);

  faqList.value.splice(index, 1, updatedFaq);
};

const onDeleteFaq = async (id: string) => {
  const isDeleteConfirm = await showConfirmPopupDialog({
    title: 'Are you sure to delete this FAQ?',
    confirmButtonText: 'Delete',
  });

  if (!isDeleteConfirm) {
    return;
  }

  const loading = ElLoading.service({
    lock: true,
    text: 'Deleting FAQ...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    await operatorFaqService.deleteOperatorFaq(id);

    faqList.value = faqList.value.filter((item: OperatorFaq) => item.id !== id);

    filterFaqs();
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  loading.close();
};

const onViewChanges = (id: string) => {
  faq.value = faqList.value.find((item: OperatorFaq) => item.id === id)!;

  changes.value = faq.value.changes || [];
  isShowChangesDialog.value = true;
};

watch(category, filterFaqs);

onMounted(() => {
  util.moveToTop();
  loadAllFAQs();
});
</script>

<style lang="scss">
.faq-list {
  margin-bottom: 30px;

  .actions-wrapper {
    display: flex;
    padding-bottom: 10px;
    border-bottom: 1px solid #deddda;
    margin-bottom: 10px;
    justify-content: flex-end;

    .el-input.el-input-group {
      flex: 1;
    }
  }
}
</style>
